import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { SIGNUP_ENABLED } from 'constants/app-info';
import { AuthLayout, AuthSwitch } from 'layouts/auth';
import { paths } from 'routes/routes';
import DoneStep from './DoneStep';
import SignUpForm from './SignUpForm';
import type { SignUpValues } from './SignUpForm';
import VerifyStep from './VerifyStep';
import type { RegisterPayload } from './register-api';

/**
 * /register — đăng ký 3 bước (đặc tả §4 phương án A, theo quyết định của chủ dự án):
 * 1. Form như app (Email, Số điện thoại, Biệt danh, Mật khẩu, TikTok ID, đồng ý Quy tắc cộng
 *    đồng) → POST auth/register. Không có Phòng ban / Ca / Mã HP Media.
 * 2. Mã 4 số gửi qua email → POST users/verify.
 * 3. Tài khoản đã tạo (USER) + 3 bước để được Admin cấp quyền vào trang quản trị.
 *
 * Dữ liệu (kể cả mật khẩu, cần cho "Gửi lại mã") chỉ nằm trong state của trang, không lên URL
 * hay storage: tải lại trang là về bước 1. Tắt bằng REACT_APP_ENABLE_SIGNUP=false (routes.tsx đã
 * chuyển /register sang /request-access; kiểm tra lại ở đây cho chắc).
 */

type Step = 'form' | 'verify' | 'done';

const TOTAL_STEPS = 3;

interface Registration {
  payload: RegisterPayload;
  /** Email backend xác nhận (app đọc response.data.email). */
  email: string;
}

const RegisterWizard = () => {
  const [step, setStep] = useState<Step>('form');
  const [registration, setRegistration] = useState<Registration | null>(null);
  const firstRender = useRef(true);

  // Đổi bước: về đầu trang (trên điện thoại nút gửi nằm cuối trang, bước mới bắt đầu từ trên).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0 });
  }, [step]);

  const email = registration?.email || '';
  const loginSwitch = (
    <AuthSwitch
      prompt="Đã có tài khoản?"
      to={paths.login}
      label="Đăng nhập"
      state={email ? { email } : undefined}
    />
  );

  if (step === 'verify' && registration) {
    return (
      <AuthLayout
        title="Xác minh email"
        // Như màn "Xác minh email" của app; email hiện ở thẻ thư ngay đầu thân thẻ.
        subtitle="Nhập mã xác nhận để kích hoạt tài khoản của bạn."
        step={{ current: 2, total: TOTAL_STEPS }}
        cardFooter={loginSwitch}
      >
        <VerifyStep
          email={email}
          payload={registration.payload}
          onVerified={() => setStep('done')}
          onEdit={() => setStep('form')}
        />
      </AuthLayout>
    );
  }

  if (step === 'done' && registration) {
    return (
      <AuthLayout
        title="Tài khoản đã tạo"
        subtitle="Admin cần cấp quyền MOD trước khi bạn vào được trang quản trị."
        step={{ current: 3, total: TOTAL_STEPS }}
        cardFooter={
          <AuthSwitch
            prompt="Muốn nhắn Admin ngay?"
            to={paths.requestAccess}
            state={{ email }}
            label="Soạn lời nhắn xin quyền"
          />
        }
      >
        <DoneStep email={email} />
      </AuthLayout>
    );
  }

  const initialValues: Partial<SignUpValues> | undefined = registration
    ? { ...registration.payload, email: registration.email, agree: true }
    : undefined;

  return (
    <AuthLayout
      title="Tạo tài khoản"
      subtitle="Tài khoản mới là tài khoản thường. Admin cần cấp quyền MOD trước khi bạn vào được trang quản trị."
      step={{ current: 1, total: TOTAL_STEPS }}
      cardFooter={
        <>
          {loginSwitch}
          <AuthSwitch
            prompt="Đã dùng app HP Media?"
            to={paths.requestAccess}
            label="Xem cách được cấp quyền"
          />
        </>
      }
    >
      <SignUpForm
        initialValues={initialValues}
        onRegistered={(payload, confirmedEmail) => {
          setRegistration({ payload, email: confirmedEmail });
          setStep('verify');
        }}
      />
    </AuthLayout>
  );
};

const RegisterPage = () =>
  SIGNUP_ENABLED ? <RegisterWizard /> : <Navigate to={paths.requestAccess} replace />;

export default RegisterPage;
